import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import {
  ExButton,
  ExInput,
  RULES_PATTERN_EMAIL,
  RULES_PATTERN_PASSWORD,
  RULES_PATTERN_PHONE_NUMBER,
  RULES_PHONE_NUMBER_MAX_LENGTH,
  RULES_REQUIERD,
  useResizeObserver,
  useToastMessage,
} from "@energyx-dev/energyx-ui";
import { useMutation } from "@tanstack/react-query";
import { postFindPwdVerification, putResetPassword } from "api/password";
import { AxiosError } from "axios";
import { DEVICE } from "constants/device";
import {
  EN_RULES_PATTERN_EMAIL,
  EN_RULES_PATTERN_PASSWORD,
  EN_RULES_PATTERN_PHONE_NUMBER,
  EN_RULES_PHONE_NUMBER_MAX_LENGTH,
  EN_RULES_REQUIERD,
} from "constants/rules";
import { useFetchSSE } from "hooks/useFetchSSE";
import useTranslation from "hooks/useTranslation";
import { ElementRef, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { TMember } from "types/auth/authTypes";
import { TVerificationSseRes } from "types/signup/signUpTypes";
import { removeHyphen } from "utils/formatters";

type TRegularPasswordChangeProps = {
  name: "phoneNumber" | "email";
  memberType: TMember;
};

export const CertificationPassword = ({ name, memberType }: TRegularPasswordChangeProps) => {
  const navigate = useNavigate();
  const [showTimer, setShowTimer] = useState(false);
  const device = useResizeObserver();
  const theme = useTheme();
  const { showToastMsg } = useToastMessage();
  const { getValues, trigger, setError } = useFormContext();
  const timer = useRef<ElementRef<typeof ExInput>>(null);
  const isEnglishPage = useTranslation();
  const searchParams = new URLSearchParams(location.search);
  const callbackUrl = searchParams.get("callbackUrl");

  const [phoneNumberVerificationResult, setPhoneNumberVerificationResult] =
    useState<TVerificationSseRes>({ verified: false, code: "" });
  const isDesktop = device === DEVICE.DESKTOP;
  const [emailVerificationResult, setEmailVerificationResult] = useState<TVerificationSseRes>({
    verified: false,
    code: "",
  });
  const { fetchSse } = useFetchSSE();
  const { mutateAsync: mutateResetPassword, isLoading } = useMutation(putResetPassword, {
    onSuccess: () => {
      showToastMsg({
        message: isEnglishPage ? "Your password has been reset." : "비밀번호를 재설정했습니다.",
        status: "normal",
        type: "single-none",
      });

      setTimeout(() => {
        if (isEnglishPage) {
          navigate(`/login?callbackUrl=${callbackUrl}&lang=en`);
          return;
        }
        navigate("/login");
      }, 2000);
    },
    onError(err) {
      console.error("err", err);
    },
  });
  const { mutateAsync: sendSmsNEmailVerification, isLoading: findPwdLoading } = useMutation(
    postFindPwdVerification,
    {
      onSuccess: async (data, variables) => {
        showToastMsg({
          message:
            variables.type === "phoneNumber"
              ? isEnglishPage
                ? "Your password has been reset."
                : "인증 문자를 전송했습니다."
              : isEnglishPage
              ? "Verification email has been sent."
              : "인증 메일을 전송했습니다.",
          status: "normal",
          type: "single-none",
        });

        setShowTimer(true);

        timer.current?.timerStart();

        await fetchSse({
          ...(name === "phoneNumber"
            ? {
                phoneNumber: removeHyphen(getValues(name)),
                to: "sms",
                setResult: setPhoneNumberVerificationResult,
                memberType,
                verificationId: data,
              }
            : {
                email: getValues(name),
                to: "email",
                setResult: setEmailVerificationResult,
                memberType,
                verificationId: data,
              }),
        });
      },
      onError: (error, variables) => {
        const _error = error as AxiosError;
        if (_error?.response?.status === 404) {
          if (variables.type === "phoneNumber") {
            setError("phoneNumber", {
              message: isEnglishPage
                ? "Please confirm your mobile phone number."
                : "휴대폰 번호를 확인해주세요",
            });
          } else {
            setError("email", {
              message: isEnglishPage
                ? "Please confirm your e-mail address."
                : "이메일 주소를 확인해주세요",
            });
          }
        }
      },
    }
  );
  // 비밀번호 재설정 버튼 클릭
  const changePasswordHandleClick = async () => {
    if (!(await trigger(["password", "passwordCheck"]))) return;
    mutateResetPassword({
      password: getValues("password"),
      type: memberType,
      ...(memberType === "corporation" && {
        resetType: name === "email" ? "EMAIL" : "PHONE_NUMBER",
      }),
      authInfo:
        name === "phoneNumber" ? removeHyphen(getValues("phoneNumber")) : getValues("email"),
      verifiedTokenId:
        name === "phoneNumber"
          ? (phoneNumberVerificationResult.smsVerifiedTokenId as string)
          : (emailVerificationResult.emailVerifiedTokenId as string),
    });
  };

  // 인증요청 버튼 클릭
  const handleClick = async () => {
    if (!(await trigger(name))) return;
    sendSmsNEmailVerification({
      memberType,
      type: name,
      ...(memberType === "normal"
        ? {
            phoneNumber: removeHyphen(getValues("phoneNumber")),
          }
        : {
            ...(name === "email"
              ? { email: getValues("email") }
              : { phoneNumber: removeHyphen(getValues("phoneNumber")) }),
          }),
    });
  };

  // input 값 변경시 timer 사라짐
  const handleChange = () => {
    setShowTimer(false);
    timer.current?.timerInit();
  };

  useEffect(() => {
    if (phoneNumberVerificationResult.code) {
      timer.current?.timerInit();

      return;
    }
    if (emailVerificationResult.code) {
      timer.current?.timerInit();
    }
  }, [phoneNumberVerificationResult, emailVerificationResult]);

  return (
    <Layout>
      <RequestWrap>
        <ExInput
          type="input"
          inputSize={device === DEVICE.MOBILE ? "mobile" : "desktop"}
          name={name}
          placeholder={
            name === "phoneNumber"
              ? isEnglishPage
                ? memberType === "corporation"
                  ? "Contract(Mobile)"
                  : "Mobile Phone"
                : "휴대폰 번호"
              : isEnglishPage
              ? "E-mail"
              : "이메일 주소"
          }
          label={
            name === "phoneNumber"
              ? isEnglishPage
                ? "Mobile Phone"
                : "휴대폰 번호"
              : isEnglishPage
              ? "E-mail"
              : "이메일 주소"
          }
          ref={timer}
          timerAdornment={name === "phoneNumber" ? 300 : 900}
          timerFinishedMessage={
            isEnglishPage ? "Your verification time has expired." : "인증 시간이 만료되었습니다."
          }
          callbackOnChange={handleChange}
          rules={{
            ...(name === "phoneNumber"
              ? isEnglishPage
                ? {
                    ...EN_RULES_PATTERN_PHONE_NUMBER,
                    ...EN_RULES_PHONE_NUMBER_MAX_LENGTH,
                    ...EN_RULES_REQUIERD,
                  }
                : {
                    ...RULES_PATTERN_PHONE_NUMBER,
                    ...RULES_PHONE_NUMBER_MAX_LENGTH,
                    ...RULES_REQUIERD,
                  }
              : isEnglishPage
              ? { ...EN_RULES_PATTERN_EMAIL, ...EN_RULES_REQUIERD }
              : { ...RULES_PATTERN_EMAIL, ...RULES_REQUIERD }),
          }}
          readonly={
            emailVerificationResult.code
              ? true
              : false || phoneNumberVerificationResult.code
              ? true
              : false
          }
          phoneNumAutoHyphen={name === "phoneNumber"}
        />
        <div>
          {emailVerificationResult.code || phoneNumberVerificationResult.code ? (
            <ExButton
              variant="contained"
              size="small"
              label={isEnglishPage ? "Verified" : "인증 완료"}
              {...(isDesktop ? { width: 86 } : { width: 84 })}
              backgroundColor={theme.color.green[10]}
              labelColor={theme.color.green[60]}
            />
          ) : (
            <ExButton
              label={
                showTimer
                  ? isEnglishPage
                    ? "Re-request"
                    : "재요청"
                  : isEnglishPage
                  ? "Verify"
                  : "인증 요청"
              }
              size="small"
              variant="contained"
              {...(isDesktop ? { width: 86 } : { width: 84 })}
              onClick={handleClick}
            />
          )}
        </div>
      </RequestWrap>
      {(phoneNumberVerificationResult.code || emailVerificationResult.code) && (
        <NewPassword>
          <div>
            <div>
              <p>
                {isEnglishPage
                  ? `${
                      emailVerificationResult.name || phoneNumberVerificationResult.name
                    }, Please enter a new password.`
                  : `${
                      emailVerificationResult.name || phoneNumberVerificationResult.name
                    }님, 새 비밀번호를 입력해주세요.`}
              </p>
            </div>
            <ExInput
              type="input"
              inputSize={device === DEVICE.MOBILE ? "mobile" : "desktop"}
              name="password"
              placeholder={isEnglishPage ? "New password" : "새 비밀번호"}
              label={isEnglishPage ? "New password" : "새 비밀번호"}
              caption={
                isEnglishPage
                  ? "At least 6 characters using a combination of alphanumeric characters."
                  : "영문과 숫자를 조합하여 6자 이상 입력해 주세요."
              }
              password
              rules={
                isEnglishPage
                  ? {
                      ...EN_RULES_REQUIERD,
                      ...EN_RULES_PATTERN_PASSWORD,
                      validate: async () => {
                        if (getValues("passwordCheck")) {
                          await trigger("passwordCheck");
                        }
                        return true;
                      },
                    }
                  : {
                      ...RULES_REQUIERD,
                      ...RULES_PATTERN_PASSWORD,
                      validate: async () => {
                        if (getValues("passwordCheck")) {
                          await trigger("passwordCheck");
                        }
                        return true;
                      },
                    }
              }
            />
            <ExInput
              type="input"
              inputSize={device === DEVICE.MOBILE ? "mobile" : "desktop"}
              name="passwordCheck"
              placeholder={isEnglishPage ? "Confirm New Password" : "새 비밀번호 확인"}
              label={isEnglishPage ? "Confirm New Password" : "새 비밀번호 확인"}
              caption={
                isEnglishPage
                  ? "Please enter your password again."
                  : "비밀번호를 한 번 더 입력해 주세요."
              }
              password
              rules={
                isEnglishPage
                  ? {
                      ...EN_RULES_REQUIERD,
                      validate: (value) =>
                        getValues("password") === value || "Please re-enter the same password.",
                    }
                  : {
                      ...RULES_REQUIERD,
                      validate: (value) =>
                        getValues("password") === value || "동일한 비밀번호를 입력해 주세요",
                    }
              }
            />
          </div>
          <ExButton
            variant="contained"
            size={device === DEVICE.MOBILE ? "large" : "xLarge"}
            label={isEnglishPage ? "Reset Password" : "비밀번호 재설정"}
            disabled={isLoading || findPwdLoading}
            backgroundColor={isEnglishPage ? "#2F2F30" : ""}
            onClick={changePasswordHandleClick}
          />
        </NewPassword>
      )}
    </Layout>
  );
};
const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const RequestWrap = styled.div`
  display: flex;
  gap: 24px;

  > div:last-of-type {
    margin-top: 12px;

    ${({ theme }) => theme.breakpoints.down("TABLET")} {
      margin-top: 7px;
    }
  }

  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    gap: 16px;
  }
`;

const NewPassword = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  > div:first-of-type {
    display: flex;
    flex-direction: column;
    gap: 16px;

    > div:first-of-type {
      display: flex;
      flex-direction: column;
      gap: 8px;

      > p {
        ${({ theme }) => theme.font.Subtitle}
        color: ${({ theme }) => theme.color.mono[80]}
      }

      > span {
        ${({ theme }) => theme.font.Caption}
        color: ${({ theme }) => theme.color.mono[60]}
      }
    }
  }
`;
